// mission-declared-resources.ts
//
// P25, spec §8 condition 4 ("đụng ranh giới scope"). `resource_names` in
// init-schema.ts names the resources OUTSIDE the mission repo that a mission
// is permitted to touch — a Mattermost channel, a GitLab project, a bucket, a
// database. It is declared by the PERSON running `avatar autopilot init`.
//
// That provenance is the whole point. `g2-plan-gate-decision.ts` warns whoever
// closes this condition NOT to reach for a worker-declared ticket field: that
// hands the decision to the party the gate governs (the Bug K shape, where a
// worker proposed `kind: "survey"` to dodge G0/G1). `resource_names` is out of
// a worker's reach by construction — it is fixed at init, before any worker
// runs, and lives in the control root the worker's worktree cannot write.
//
// Before this file the field had the Bug J shape instead: InitRequestSchema
// accepted and validated it, then `initMission` kept only
// `repo`/`mode`/`blueprint.bytes_sha256` and dropped the rest. Exactly what
// happened to `actorOsAccount` — both ends correct, the join missing — which
// is why §8 condition 4 sat in `unenforcedConditions` for eight phases.
//
// `undefined` vs `[]` is load-bearing and must not be collapsed. `[]` means
// "the person declared no external resources"; `undefined` means "nobody ever
// declared" (a mission initialised before P25, or an unreadable file). The G2
// decision treats BOTH as a reason to ask a human, but for different stated
// reasons — collapsing them into one would be the swallowed-state class of bug
// this project has paid for three times (P20 Lỗi A, P21 promote result, P24
// release flow outcome).
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";

const DECLARED_RESOURCES_FILE_NAME = "mission-declared-resources.json";

function declaredResourcesPath(controlRoot: string): string {
  return join(controlRoot, DECLARED_RESOURCES_FILE_NAME);
}

/**
 * Persists the resource boundary declared at `autopilot init`. Written once,
 * at init, from the validated request — never from worker output.
 */
export async function writeMissionDeclaredResources(
  controlRoot: string,
  resourceNames: readonly string[],
  now: () => Date,
): Promise<void> {
  const payload = {
    schema_version: 1,
    resource_names: [...resourceNames],
    declared_at: now().toISOString(),
  };
  await writeFileDurable(
    declaredResourcesPath(controlRoot),
    Buffer.from(`${JSON.stringify(payload, null, 2)}\n`, "utf8"),
  );
}

/**
 * Reads the declared boundary. `undefined` means "unknown" — a missing or
 * unreadable file — and callers must NOT read it as "no resources declared":
 * an unknown boundary is a reason to ask a human, not permission to skip the
 * question. A declared empty list comes back as `[]`.
 */
export async function readMissionDeclaredResources(
  controlRoot: string,
): Promise<string[] | undefined> {
  let raw: string;
  try {
    raw = await readFile(declaredResourcesPath(controlRoot), "utf8");
  } catch {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as { resource_names?: unknown };
    const names = parsed.resource_names;
    if (!Array.isArray(names)) return undefined;
    // A hand-edited file with the wrong shape reads as UNKNOWN rather than
    // being coerced: a boundary nobody can parse is not a boundary.
    if (!names.every((n): n is string => typeof n === "string")) return undefined;
    return names;
  } catch {
    return undefined;
  }
}
